import XLSX from 'xlsx-js-style';
import dayjs from 'dayjs';
import { calculateTotalRow } from '../Formulas/ReportFormulas';
import { inferColumnDecimals, looksLikeDecimalColumn } from '../HelperFunctions/ReportHelpers';

export const exportToExcel = (data, reportPayload, pickupDaysLabel, selectedRows, displaySettings, dateRange, showLegalName, totals) => {
  // 1. Setup Data & Structure
  const isMultiple = selectedRows?.length > 1 || (reportPayload?.CorporationIDLst && String(reportPayload.CorporationIDLst).includes(','));

  const revenueKeys = [
    "OTBADR", "OTBRoomRev", "OTBRevPar",
    "PickUpADR", "PickUpRev", "PickUpRoomRev",
    "BudgetAdr", "BudgetRoomRev", "BudgetRevPar",
    "ADRVariance", "RoomRevenueVariance",
    "LYADR", "LYRoomRev", "LYRevPar",
    "LYADRVariance", "LYRoomRevenueVariance"
  ];
  const occupancyKeys = [
    "OTBOcc", "PickUpOcc", "BudgetOcc", "OccVariance", "LYOccupancy", "LYOccVariance"
  ];

  const reportStructure = [
    {
      title: "Details",
      columns: isMultiple ? [
        { label: "Corp Name", key: "CorporationName", dataKey: showLegalName ? "CorpLegalName" : "Corpname" },
        { label: "Legal Name", key: "LegalName", dataKey: "PCName" },
        { label: "Brand Name", key: "BrandName", dataKey: "BrandName" },
        { label: "Total Rooms", key: "TotalRooms", dataKey: "TotRooms" },
        { label: "Trns Rooms", key: "TrnsRooms", dataKey: "TransRooms" },
        { label: "Grp Rooms", key: "GrpRooms", dataKey: "GrpRooms" },
      ] : [
        { label: "Date", key: "Date", dataKey: "SaleDate" },
        { label: "DOW", key: "DOW", dataKey: "Dayweek" },
        { label: "Total Rooms", key: "TotalRooms", dataKey: "TotRooms" },
        { label: "Trns Rooms", key: "TrnsRooms", dataKey: "TransRooms" },
        { label: "Grp Rooms", key: "GrpRooms", dataKey: "GrpRooms" },
      ]
    },
    {
      title: "OTB Actuals",
      columns: [
        { label: "Curr Rooms Sold", key: "OTBRoomsSold", dataKey: "CurrRoomsSold" },
        { label: "Curr Occ", key: "OTBOcc", dataKey: "CurrOcc" },
        { label: "Curr ADR", key: "OTBADR", dataKey: "CurrADR" },
        { label: "Curr Rev Par", key: "OTBRevPar", dataKey: "CurrRevPar" },
        { label: "Curr Room Rev", key: "OTBRoomRev", dataKey: "CurrRoomRev" },
      ]
    },
    {
      title: pickupDaysLabel ? `${pickupDaysLabel} Pickup` : "Pickup",
      columns: [
        { label: "Rooms", key: "PickUpRooms", dataKey: "PickUpRooms" },
        { label: "Occ", key: "PickUpOcc", dataKey: "PickUpOcc" },
        { label: "ADR", key: "PickUpADR", dataKey: "PickUpADR" },
        { label: "Rev", key: "PickUpRev", dataKey: "PickUpRoomRev" },
      ]
    },
    {
      title: "Budget",
      columns: [
        { label: "Bud Sold", key: "BudgetRoomsSold", dataKey: "BudRoomsSold" },
        { label: "Bud Occ %", key: "BudgetOcc", dataKey: "BudOcc" },
        { label: "Bud ADR", key: "BudgetAdr", dataKey: "BudADR" },
        { label: "Bud RevPAR", key: "BudgetRevPar", dataKey: "BudRevPar" },
        { label: "Bud Rm Rev", key: "BudgetRoomRev", dataKey: "BudRoomRev" },
      ]
    },
    {
      title: "Day Variance",
      columns: [
        { label: "Rms(Bud)", key: "RoomsSoldVariance", dataKey: "RoomsSoldVar" },
        { label: "Occ %(Bud)", key: "OccVariance", dataKey: "OccVar" },
        { label: "ADR(Bud)", key: "ADRVariance", dataKey: "ADRVar" },
        { label: "Rev(Bud)", key: "RoomRevenueVariance", dataKey: "RoomRevVar" },
      ]
    },
    {
      title: "LY Actuals",
      columns: [
        { label: "LY Sold", key: "LYRoomsSold", dataKey: "LYRoomsSold" },
        { label: "LY Occ%", key: "LYOccupancy", dataKey: "LYOcc" },
        { label: "LY ADR", key: "LYADR", dataKey: "LYADR" },
        { label: "LY RevPAR", key: "LYRevPar", dataKey: "LYRevPar" },
        { label: "LY Rm Rev", key: "LYRoomRev", dataKey: "LYRoomRev" },
      ]
    },
    {
      title: "Day Variance",
      columns: [
        { label: "Rms(LY)", key: "LYRoomsSoldVariance", dataKey: "LYRoomsSoldVar" },
        { label: "Occ %(LY)", key: "LYOccVariance", dataKey: "LYOccVar" },
        { label: "ADR(LY)", key: "LYADRVariance", dataKey: "LYADRVar" },
        { label: "Rev(LY)", key: "LYRoomRevenueVariance", dataKey: "LYRoomRevVar" },
      ]
    }
  ];

  const filteredStructure = (pickupDaysLabel && pickupDaysLabel.toLowerCase() === 'n/a') ? reportStructure.filter(s => !s.title.toLowerCase().includes('pickup')) : reportStructure;

  const visibleSections = filteredStructure.map(section => ({
    title: section.title,
    columns: section.columns.filter(col => {
      if (isMultiple && (col.key === 'CorporationName' || col.key === 'LegalName' || col.key === 'BrandName')) return true;
      return reportPayload && reportPayload[col.key];
    })
  })).filter(section => section.columns.length > 0);

  const formatText = (text) => {
    if (!text || typeof text !== 'string') return text;
    if (displaySettings?.textFormat === 'upper') return text.toUpperCase();
    if (displaySettings?.textFormat === 'lower') return text.toLowerCase();
    return text;
  };

  // Formula Definitions
  const reportFormulas = {
    OTBOcc: (r, c) => `IF(${c.TotalRooms}${r}=0,0,(${c.OTBRoomsSold}${r}/${c.TotalRooms}${r})*100)`,
    OTBADR: (r, c) => `IF(${c.OTBRoomsSold}${r}=0,0,${c.OTBRoomRev}${r}/${c.OTBRoomsSold}${r})`,
    OTBRevPar: (r, c) => `IF(${c.TotalRooms}${r}=0,0,${c.OTBRoomRev}${r}/${c.TotalRooms}${r})`,

    PickUpOcc: (r, c) => `IF(${c.TotalRooms}${r}=0,0,(${c.PickUpRooms}${r}/${c.TotalRooms}${r})*100)`,
    PickUpADR: (r, c) => `IF(${c.PickUpRooms}${r}=0,0,${c.PickUpRev}${r}/${c.PickUpRooms}${r})`,

    BudgetOcc: (r, c) => `IF(${c.TotalRooms}${r}=0,0,(${c.BudgetRoomsSold}${r}/${c.TotalRooms}${r})*100)`,
    BudgetRoomRev: (r, c) => `${c.BudgetRoomsSold}${r}*${c.BudgetAdr}${r}`,
    BudgetRevPar: (r, c) => `IF(${c.TotalRooms}${r}=0,0,${c.BudgetRoomRev}${r}/${c.TotalRooms}${r})`,

    LYOccupancy: (r, c) => `IF(${c.TotalRooms}${r}=0,0,(${c.LYRoomsSold}${r}/${c.TotalRooms}${r})*100)`,
    LYRoomRev: (r, c) => `${c.LYRoomsSold}${r}*${c.LYADR}${r}`,
    LYRevPar: (r, c) => `IF(${c.TotalRooms}${r}=0,0,${c.LYRoomRev}${r}/${c.TotalRooms}${r})`,

    RoomsSoldVariance: (r, c) => `${c.OTBRoomsSold}${r}-${c.BudgetRoomsSold}${r}`,
    ADRVariance: (r, c) => `${c.OTBADR}${r}-${c.BudgetAdr}${r}`,
    RoomRevenueVariance: (r, c) => `${c.OTBRoomRev}${r}-${c.BudgetRoomRev}${r}`,
    OccVariance: (r, c) => `${c.OTBOcc}${r}-${c.BudgetOcc}${r}`,

    LYRoomsSoldVariance: (r, c) => `${c.OTBRoomsSold}${r}-${c.LYRoomsSold}${r}`,
    LYADRVariance: (r, c) => `${c.OTBADR}${r}-${c.LYADR}${r}`,
    LYRoomRevenueVariance: (r, c) => `${c.OTBRoomRev}${r}-${c.LYRoomRev}${r}`,
    LYOccVariance: (r, c) => `${c.OTBOcc}${r}-${c.LYOccupancy}${r}`,
  };

  const baseSumColumns = [
    "TotalRooms", "TrnsRooms", "GrpRooms",
    "OTBRoomsSold", "OTBRoomRev",
    "PickUpRooms", "PickUpRev",
    "BudgetRoomsSold", "BudgetRoomRev",
    "LYRoomsSold", "LYRoomRev"
  ];

  // 2. Prepare Header Rows & Merges
  const headerRow1 = [];
  const headerRow2 = [];
  const merges = [];
  const columnKeys = [];
  const visibleColumns = [];
  let colIndex = 0;

  visibleSections.forEach(section => {
    const colCount = section.columns.length;
    headerRow1.push(formatText(section.title));
    for (let i = 1; i < colCount; i++) headerRow1.push("");

    section.columns.forEach(col => {
      headerRow2.push(formatText(col.label));
      columnKeys.push(col.key);
      visibleColumns.push(col);
    });

    if (colCount > 1) {
      merges.push({ s: { r: 0, c: colIndex }, e: { r: 0, c: colIndex + colCount - 1 } });
    }
    colIndex += colCount;
  });

  // Prepare Title Rows
  const titleRows = [];
  titleRows.push(["OTB Report"]);

  const fromDate = dateRange?.[0] ? dayjs(dateRange[0]).format('MMMM DD, YYYY') : '';
  const toDate = dateRange?.[1] ? dayjs(dateRange[1]).format('MMMM DD, YYYY') : '';
  titleRows.push([`${fromDate} To ${toDate}`]);

  if (pickupDaysLabel && pickupDaysLabel.toLowerCase() !== 'n/a') {
    titleRows.push([`${pickupDaysLabel} Pickup`]);
  }

  let nameRowIndex = -1;
  let corpName = '';
  let pcName = '';

  if (selectedRows && selectedRows.length > 1) {
    corpName = 'Multiple';
    pcName = 'Multiple';
  } else if (data && data.length > 0) {
    corpName = (showLegalName ? data[0].CorpLegalName : data[0].Corpname) || data[0].Corpname || '';
    pcName = data[0].PCName || '';
  } else if (selectedRows && selectedRows.length === 1) {
    corpName = selectedRows[0].corpName || '';
  }

  if (corpName || pcName) {
    const totalCols = headerRow2.length;
    const nameRow = new Array(totalCols).fill("");
    if (totalCols > 0) {
      nameRow[0] = `${formatText("Corporation/Legal Name")} : ${formatText(corpName)}`;
      if (pcName && totalCols > 1) {
        const mid = Math.ceil(totalCols / 2);
        nameRow[mid] = `${formatText("Profit Center")} : ${formatText(pcName)}`;
      }
    }
    titleRows.push(nameRow);
    nameRowIndex = titleRows.length - 1;
  }

  const titleRowCount = titleRows.length;

  // Shift existing merges for headers
  merges.forEach(m => {
    m.s.r += titleRowCount;
    m.e.r += titleRowCount;
  });

  // Add merges for title rows
  const totalCols = headerRow2.length;
  if (totalCols > 0) {
    for (let i = 0; i < titleRowCount; i++) {
      if (i === nameRowIndex) {
        if (pcName && totalCols > 1) {
          const mid = Math.ceil(totalCols / 2);
          merges.push({ s: { r: i, c: 0 }, e: { r: i, c: mid - 1 } });
          merges.push({ s: { r: i, c: mid }, e: { r: i, c: totalCols - 1 } });
        } else {
          merges.push({ s: { r: i, c: 0 }, e: { r: i, c: totalCols - 1 } });
        }
        continue;
      }
      merges.push({ s: { r: i, c: 0 }, e: { r: i, c: totalCols - 1 } });
    }
  }

  // 3. Process Data & Totals
  let safeData = Array.isArray(data) ? data : [];

  if (displaySettings?.excludeZero) {
    const isNumericColumn = (key) => !['CorporationName', 'BrandName', 'LegalName', 'Date', 'DOW', 'PCName', 'CorpLegalName'].includes(key);
    const numericKeysToCheck = [];
    visibleSections.forEach(section => {
      section.columns.forEach(col => {
        if (isNumericColumn(col.key)) {
          numericKeysToCheck.push(col.dataKey);
        }
      });
    });

    if (numericKeysToCheck.length > 0) {
      safeData = safeData.filter(row => {
        return numericKeysToCheck.some(key => {
          const val = parseFloat(row[key]);
          return !isNaN(val) && Math.abs(val) > 0.000001;
        });
      });
    }
  }

  // Initialize Total Row
  const calculatedTotal = calculateTotalRow(safeData, totals) || {};
  const totalRow = {
    SaleDate: 'Total', Corpname: 'Total', BrandName: '', PCName: '', Dayweek: '',
    // TotRooms: 0, TransRooms: 0, GrpRooms: 0,
    // CurrRoomsSold: 0, CurrRoomRev: 0,
    // PickUpRooms: 0, PickUpRoomRev: 0,
    // BudRoomsSold: 0, BudRoomRev: 0,
    // LYRoomsSold: 0, LYRoomRev: 0
    ...calculatedTotal
  };
  const pickupDataKeys = ["PickUpRooms", "PickUpOcc", "PickUpADR", "PickUpRoomRev"];
  if (safeData.length > 0) {
    pickupDataKeys.forEach(key => {
      if (safeData.every(row => row[key] === 'N/A')) {
        totalRow[key] = 'N/A';
      }
    });
  }

  const columnDecimals = inferColumnDecimals(safeData);

  const getColDecimals = (col) => {
    if (displaySettings?.withoutCents) return 0;
    if (col?.dataKey && columnDecimals[col.dataKey] !== undefined) return columnDecimals[col.dataKey];
    return (looksLikeDecimalColumn(col?.dataKey) || looksLikeDecimalColumn(col?.key)) ? 2 : 0;
  };

  // Accumulate Totals
  // safeData.forEach(row => {
  //   totalRow.TotRooms += Number(row.TotRooms) || 0;
  //   totalRow.TransRooms += Number(row.TransRooms) || 0;
  //   totalRow.GrpRooms += Number(row.GrpRooms) || 0;
  //   totalRow.CurrRoomsSold += Number(row.CurrRoomsSold) || 0;
  //   totalRow.CurrRoomRev += Number(row.CurrRoomRev) || 0;
  //   totalRow.PickUpRooms += Number(row.PickUpRooms) || 0;
  //   totalRow.PickUpRoomRev += Number(row.PickUpRoomRev) || 0;
  //   totalRow.BudRoomsSold += Number(row.BudRoomsSold) || 0;
  //   totalRow.BudRoomRev += Number(row.BudRoomRev) || 0;
  //   totalRow.LYRoomsSold += Number(row.LYRoomsSold) || 0;
  //   totalRow.LYRoomRev += Number(row.LYRoomRev) || 0;
  // });

  // // Round Totals
  // Object.keys(totalRow).forEach(key => {
  //   if (typeof totalRow[key] === 'number') totalRow[key] = Number(totalRow[key].toFixed(2));
  // });

  // // Calculate Derived Stats
  // const calcRate = (rev, sold) => sold ? Number((rev / sold).toFixed(2)) : 0;
  // const calcOcc = (sold, total) => total ? Number(((sold / total) * 100).toFixed(2)) : 0;
  // const calcRevPar = (rev, total) => total ? Number((rev / total).toFixed(2)) : 0;

  // totalRow.CurrOcc = calcOcc(totalRow.CurrRoomsSold, totalRow.TotRooms);
  // totalRow.CurrADR = calcRate(totalRow.CurrRoomRev, totalRow.CurrRoomsSold);
  // totalRow.CurrRevPar = calcRevPar(totalRow.CurrRoomRev, totalRow.TotRooms);

  // totalRow.PickUpOcc = calcOcc(totalRow.PickUpRooms, totalRow.TotRooms);
  // totalRow.PickUpADR = calcRate(totalRow.PickUpRoomRev, totalRow.PickUpRooms);

  // totalRow.BudOcc = calcOcc(totalRow.BudRoomsSold, totalRow.TotRooms);
  // totalRow.BudADR = calcRate(totalRow.BudRoomRev, totalRow.BudRoomsSold);
  // totalRow.BudRevPar = calcRevPar(totalRow.BudRoomRev, totalRow.TotRooms);

  // totalRow.LYOcc = calcOcc(totalRow.LYRoomsSold, totalRow.TotRooms);
  // totalRow.LYADR = calcRate(totalRow.LYRoomRev, totalRow.LYRoomsSold);
  // totalRow.LYRevPar = calcRevPar(totalRow.LYRoomRev, totalRow.TotRooms);

  // totalRow.RoomsSoldVar = Number((totalRow.CurrRoomsSold - totalRow.BudRoomsSold).toFixed(2));
  // totalRow.ADRVar = Number((totalRow.CurrADR - totalRow.BudADR).toFixed(2));
  // totalRow.RoomRevVar = Number((totalRow.CurrRoomRev - totalRow.BudRoomRev).toFixed(2));
  // totalRow.OccVar = Number((totalRow.CurrOcc - totalRow.BudOcc).toFixed(2));

  // totalRow.LYRoomsSoldVar = Number((totalRow.CurrRoomsSold - totalRow.LYRoomsSold).toFixed(2));
  // totalRow.LYADRVar = Number((totalRow.CurrADR - totalRow.LYADR).toFixed(2));
  // totalRow.LYRoomRevVar = Number((totalRow.CurrRoomRev - totalRow.LYRoomRev).toFixed(2));
  // totalRow.LYOccVar = Number((totalRow.CurrOcc - totalRow.LYOcc).toFixed(2));

  const dataWithTotal = [...safeData, totalRow];

  // Map Data Rows
  const dataRows = dataWithTotal.map(row => {
    const rowValues = [];
    visibleSections.forEach(section => {
      section.columns.forEach(col => {
        let val = row[col.dataKey];

        if ((col.key === 'BrandName' || col.key === 'CorporationName') && !val && selectedRows) {
          const matchedRow = selectedRows.find(r => r.corpName === row.Corpname);
          if (matchedRow) val = col.key === 'BrandName' ? matchedRow.brand : (showLegalName ? matchedRow.legalName : matchedRow.corpName);
        }

        if (col.key === 'LegalName' && (!val || val === 'N/A') && row.Corpname !== 'Total' && row.SaleDate !== 'Total') {
          const matchedRow = selectedRows?.find(r => r.corpName === row.Corpname);
          val = row.CorpLegalName || row.Corpname || matchedRow?.legalName || matchedRow?.corpName || '';
        }

        if (col.dataKey === 'SaleDate' && val && val !== 'Total') {
          val = dayjs(val).format('MM/DD/YYYY');
        }

        if (typeof val === "string" && (val.includes("÷") || val.includes("×"))) {
          val = 0;
        }

        const isNumeric = !['CorporationName', 'BrandName', 'LegalName', 'Date', 'DOW', 'PCName', 'CorpLegalName'].includes(col.key);
        if (isNumeric) {
          if (val !== null && val !== undefined && val !== '' && val !== 'Total') {
            const num = Number(val);
            if (!isNaN(num)) {
              if (displaySettings?.withoutCents) {
                val = Math.trunc(num);
              } else {
                const decimals = getColDecimals(col);
                val = decimals > 0 ? Number(num.toFixed(decimals)) : Math.trunc(num);
              }
            }
          }
        } else {
          val = formatText(val);
        }
        rowValues.push(val);
      });
    });
    return rowValues;
  });

  // 4. Create Sheet
  const wsData = [...titleRows, headerRow1, headerRow2, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Apply Merges
  if (merges.length > 0) ws['!merges'] = merges;

  // Apply Formulas
  const keyToColLetter = {};
  columnKeys.forEach((key, idx) => {
    keyToColLetter[key] = XLSX.utils.encode_col(idx);
  });

  const dataStartRowIndex = titleRowCount + 2; // 0-based index where data starts
  const totalRowIndex = dataStartRowIndex + safeData.length; // 0-based index of total row

  for (let r = dataStartRowIndex; r <= totalRowIndex; r++) {
    const excelRow = r + 1; // 1-based Excel row
    const isTotalRow = r === totalRowIndex;

    columnKeys.forEach((key, cIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r, c: cIndex });
      if (!ws[cellRef]) return;

      if (ws[cellRef].v === 'N/A') return;

      if (isTotalRow && baseSumColumns.includes(key)) {
        const startRef = XLSX.utils.encode_cell({ r: dataStartRowIndex, c: cIndex });
        const endRef = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c: cIndex });
        const sumFormula = `SUM(${startRef}:${endRef})`;
        ws[cellRef].f = displaySettings?.withoutCents ? `TRUNC(${sumFormula})` : sumFormula;
      } else if (reportFormulas[key]) {
        const formulaFn = reportFormulas[key];
        const formula = formulaFn(excelRow, keyToColLetter);

        if (!formula.includes("undefined")) {
          ws[cellRef].f = displaySettings?.withoutCents ? `TRUNC(${formula})` : formula;
        }
      }
    });
  }

  // Configure Views
  ws['!views'] = [{ state: 'normal', showGridLines: true, showRowColHeaders: true }];

  // Auto Column Width
  const colWidths = headerRow2.map(header => ({ wch: (header ? header.toString().length : 0) + 5 }));
  dataRows.forEach(row => {
    row.forEach((cell, i) => {
      let cellLength = 0;
      if (cell !== null && cell !== undefined) {
        cellLength = cell.toString().length;
        if (typeof cell === 'number') cellLength += 10;
      }
      if (colWidths[i] && cellLength + 2 > colWidths[i].wch) {
        colWidths[i].wch = cellLength + 2;
      }
    });
  });
  ws['!cols'] = colWidths;

  // 5. STYLING (Applied via xlsx-js-style)
  if (ws['!ref']) {
    const range = XLSX.utils.decode_range(ws['!ref']);

    // Define Base Styles
    const baseStyle = {
      font: { name: "Poppins", sz: 8 }, // Default Font
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const headerStyle = {
      ...baseStyle,
      font: { name: "Poppins", sz: 8, bold: true },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      fill: { fgColor: { rgb: "EFEFEF" } } // Optional: Light Gray Header Background
    };

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
        if (!ws[cellRef]) continue;

        // Apply Styles based on Row Type
        if (R < titleRowCount) {
          // Title Rows
          if (R === nameRowIndex) {
            ws[cellRef].s = {
              font: { name: "Poppins", sz: 8, bold: true },
              alignment: { horizontal: (pcName && totalCols > 1) ? (C < (totalCols / 2) ? "left" : "right") : "left", vertical: "center" },
              border: baseStyle.border
            };
          } else {
            ws[cellRef].s = {
              font: { name: "Poppins", sz: R === 0 ? 14 : 10, bold: true },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
        } else if (R <= titleRowCount + 1) {
          // Headers
          ws[cellRef].s = headerStyle;
        } else if (R === range.e.r) {
          // Total Row
          ws[cellRef].s = {
            ...baseStyle,
            font: { name: "Poppins", sz: 8, bold: true },
            fill: { fgColor: { rgb: "FAFAFA" } }
          };
        } else {
          // Data Rows
          ws[cellRef].s = { ...baseStyle };
        }

        // Numeric Formatting & Alignment
        if (typeof ws[cellRef].v === 'number' || (ws[cellRef].f && ws[cellRef].v !== 'N/A')) {
          if (!ws[cellRef].s.alignment) ws[cellRef].s.alignment = {};
          ws[cellRef].s.alignment.horizontal = "right";

          const col = visibleColumns[C];
          const colKey = col ? col.key : columnKeys[C];
          const decimals = col ? getColDecimals(col) : 2;
          const baseFormat = decimals > 0 ? '#,##0.00' : '#,##0';
          let prefix = "";
          let suffix = "";

          if (revenueKeys.includes(colKey)) {
            prefix = "$";
          } else if (occupancyKeys.includes(colKey)) {
            suffix = "\"%\"";
          }

          const positiveFormat = `${prefix}${baseFormat}${suffix}`;
          let negativeFormatStr = "";

          if (displaySettings?.negativeFormat === 'parentheses') {
            negativeFormatStr = `\\(${prefix}${baseFormat}${suffix}\\)`;
          } else if (displaySettings?.negativeFormat === 'trailing') {
            negativeFormatStr = `${prefix}${baseFormat}${suffix}-`;
          } else {
            negativeFormatStr = `-${prefix}${baseFormat}${suffix}`;
          }

          if (displaySettings?.showRedNegative) {
            negativeFormatStr = `[Red]${negativeFormatStr}`;
          }

          ws[cellRef].z = `${positiveFormat};${negativeFormatStr}`;
        } else if (String(ws[cellRef].v).trim().toUpperCase() === 'N/A') {
          if (!ws[cellRef].s.alignment) ws[cellRef].s.alignment = {};
          ws[cellRef].s.alignment.horizontal = "right";
        }
      }
    }
  }

  // 6. Generate & Save
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "OTB Report");
  XLSX.writeFile(wb, "OTB_Report.xlsx");
};